import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Upload, Modal, Slider, Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import imageCompression from "browser-image-compression";
import { getCroppedImageFile } from "./cropUtils";
import "./ImageUploadCrop.css";

const MAX_RAW_SIZE_MB = 10;

/**
 * Drop-in replacement for the plain antd Dragger + browser-image-compression
 * pattern used on Partners/Founder, adding a crop step locked to a given
 * aspect ratio so the saved image matches how it actually displays on the
 * public site. Calls onChange(compressedFile) with a File ready to append
 * to FormData, same shape the existing submit code already expects.
 */
function ImageUploadCrop({
  value,
  onChange,
  aspectRatio = 1,
  maxSizeMB = 1,
  maxWidthOrHeight = 1024,
  label = "Image",
}) {
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [rawFileMeta, setRawFileMeta] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [localError, setLocalError] = useState("");

  const onCropComplete = useCallback((_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleBeforeUpload = (file) => {
    setLocalError("");
    if (!file.type.startsWith("image/")) {
      setLocalError("Please choose an image file.");
      return false;
    }
    if (file.size > MAX_RAW_SIZE_MB * 1024 * 1024) {
      setLocalError(`Image must be smaller than ${MAX_RAW_SIZE_MB}MB.`);
      return false;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setRawImageSrc(reader.result);
      setRawFileMeta({ name: file.name, type: file.type });
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
    return false; // prevent antd's own upload -- we handle the file ourselves
  };

  const handleCropConfirm = async () => {
    if (!croppedAreaPixels || !rawImageSrc) return;
    setIsProcessing(true);
    try {
      const croppedFile = await getCroppedImageFile(
        rawImageSrc,
        croppedAreaPixels,
        rawFileMeta?.name || "image.jpg",
        rawFileMeta?.type?.startsWith("image/") ? rawFileMeta.type : "image/jpeg"
      );
      const compressedFile = await imageCompression(croppedFile, {
        maxSizeMB,
        maxWidthOrHeight,
        useWebWorker: true,
      });
      setPreviewUrl(URL.createObjectURL(compressedFile));
      onChange(compressedFile);
      setCropModalOpen(false);
      setRawImageSrc(null);
    } catch (err) {
      setLocalError("Failed to process image. Please try another one.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setRawImageSrc(null);
  };

  const displayUrl = previewUrl || value;

  return (
    <div className="image_upload_crop_wrap">
      <Upload
        listType="picture-card"
        showUploadList={false}
        beforeUpload={handleBeforeUpload}
        accept="image/*"
      >
        {displayUrl ? (
          <img src={displayUrl} alt={label} className="image_upload_crop_thumb" />
        ) : (
          <div className="image_upload_crop_placeholder">
            <PlusOutlined />
            <div>Upload {label}</div>
          </div>
        )}
      </Upload>
      {localError && <p className="image_upload_crop_error">{localError}</p>}

      <Modal
        open={cropModalOpen}
        onCancel={handleCropCancel}
        title={`Crop ${label}`}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={handleCropCancel}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={isProcessing}
            onClick={handleCropConfirm}
            style={{ backgroundColor: "var(--kc-maroon)", borderColor: "var(--kc-maroon)" }}
          >
            Use this image
          </Button>,
        ]}
      >
        {rawImageSrc && (
          <>
            <div className="image_upload_crop_stage">
              <Cropper
                image={rawImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspectRatio}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="image_upload_crop_zoom">
              <span>Zoom</span>
              <Slider
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={setZoom}
                style={{ flex: 1 }}
              />
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

export default ImageUploadCrop;
